import { useLiveQuery } from 'dexie-react-hooks';
import { Link } from 'react-router';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { Aguardar, BotaoLink, CabecalhoPagina, EstadoVazio, Indicador, Painel } from '../components/ui/index.js';
import { formatarData, hojeIso, idadeDoAnimal } from '../dados/catalogo.js';
import { useConsulta, useFonte, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Midia, Visita } from '../dados/fonte.js';
import { db } from '../lib/local.js';
import { proximaFicha } from './Coleta.js';
import { situacaoDaVisita } from './Visitas.js';

const fechada = (a: Avaliacao) => a.status === 'fechada' || a.status === 'validada';

/**
 * Inicio: o que fazer agora. A equipe ve a visita da vez, as fichas abertas e o
 * que ainda nao subiu; o dono do haras ve o plantel dele e os laudos recentes.
 */
export function Inicio() {
  const fonte = useFonte();
  const equipe = usePodeCadastrar();
  const consulta = useConsulta(async (f) => {
    const [usuario, haras, animais, avaliacoes, visitas, capas] = await Promise.all([
      f.usuarioAtual(),
      f.haras(),
      f.listarAnimais(),
      f.avaliacoes(),
      f.listarVisitas(),
      f.capas(),
    ]);
    return { usuario, haras, animais, avaliacoes, visitas, capas };
  }, []);

  return (
    <Aguardar consulta={consulta}>
      {({ usuario, haras, animais, avaliacoes, visitas, capas }) => {
        const primeiroNome = usuario?.nome?.split(' ')[0];
        const saudacao = `${new Date().getHours() < 12 ? 'Bom dia' : new Date().getHours() < 18 ? 'Boa tarde' : 'Boa noite'}${primeiroNome ? `, ${primeiroNome}` : ''}`;
        if (!haras && !fonte.ficticia) {
          return (
            <div className="space-y-6">
              <CabecalhoPagina titulo={saudacao} />
              <EstadoVazio
                titulo={equipe ? 'Comece pelo haras' : 'Nenhum haras liberado para você ainda'}
                acao={equipe && <BotaoLink variante="principal" to="/haras/novo">Cadastrar o primeiro haras</BotaoLink>}
              >
                {equipe
                  ? 'Depois do haras vêm os animais e o planejamento da visita.'
                  : 'O plantel aparece aqui assim que a equipe Equiview liberar o seu haras.'}
              </EstadoVazio>
            </div>
          );
        }
        const plantel = animais.filter((a) => !haras || a.owner_farm_id === haras.id);
        const doPlantel = new Set(plantel.map((a) => a.id));
        const fichas = avaliacoes.filter((a) => doPlantel.has(a.animal_id));
        return (
          <div className="space-y-6">
            <CabecalhoPagina sobretitulo={haras?.name} titulo={saudacao} />
            {equipe ? (
              <DaEquipe visitas={visitas} fichas={fichas} animais={animais} />
            ) : (
              <DoDono plantel={plantel} fichas={fichas} capas={capas} />
            )}
          </div>
        );
      }}
    </Aguardar>
  );
}

function DaEquipe({ visitas, fichas, animais }: { visitas: Visita[]; fichas: Avaliacao[]; animais: Animal[] }) {
  const porEnviar = useLiveQuery(() => db.outbox.count(), [], 0);
  const emQuarentena = useLiveQuery(() => db.quarantine.count(), [], 0);
  const daVisita = (v: Visita) => fichas.filter((a) => a.visit_id === v.id);
  // A visita da vez: a primeira em andamento, senao a proxima planejada.
  const abertas = visitas.filter((v) => !v.closed_at && situacaoDaVisita(daVisita(v), v) !== 'fechada');
  const daVez =
    abertas.find((v) => situacaoDaVisita(daVisita(v), v) === 'andamento') ??
    abertas.slice().sort((a, b) => a.visit_date.localeCompare(b.visit_date))[0];
  const proxima = daVez ? proximaFicha(daVisita(daVez), animais) : undefined;
  const fichasAbertas = fichas.filter((a) => a.started_at && !fechada(a));

  return (
    <>
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda sm:grid-cols-4">
        <Indicador rotulo="Visitas abertas" valor={abertas.length} />
        <Indicador rotulo="Fichas em coleta" valor={fichasAbertas.length} />
        <Indicador rotulo="Por enviar" valor={porEnviar} complemento="registros" />
        <Indicador rotulo="Quarentena" valor={emQuarentena} complemento={emQuarentena ? 'precisa de decisão' : 'tudo certo'} />
      </section>

      {daVez ? (
        <Painel
          titulo={`Visita nº ${daVez.visit_number} · ${formatarData(daVez.visit_date)}`}
          acoes={
            <div className="flex gap-2">
              <BotaoLink to={`/visitas/${daVez.id}`}>Ver visita</BotaoLink>
              {proxima && (
                <BotaoLink variante="principal" to={`/visitas/${daVez.id}/fichas/${proxima.id}`}>
                  {situacaoDaVisita(daVisita(daVez), daVez) === 'planejada' ? 'Iniciar coleta' : 'Continuar coleta'}
                </BotaoLink>
              )}
            </div>
          }
        >
          <p className="text-sm text-apagado">
            {daVisita(daVez).filter(fechada).length} de {daVisita(daVez).length} fichas fechadas.
            {proxima && ` Próximo animal: ${animais.find((a) => a.id === proxima.animal_id)?.name ?? '—'}.`}
          </p>
        </Painel>
      ) : (
        <EstadoVazio titulo="Nenhuma visita aberta" acao={<BotaoLink variante="principal" to="/visitas/nova">Planejar visita</BotaoLink>}>
          Planeje a próxima visita: escolha os animais e o nível de cada um.
        </EstadoVazio>
      )}

      {emQuarentena > 0 && (
        <p className="border-l-2 border-erro px-3 py-2 text-sm">
          Há {emQuarentena} registro(s) recusado(s) pelo servidor.{' '}
          <Link to="/sincronizacao" className="font-medium text-destaque-texto hover:underline">
            Resolver agora
          </Link>
        </p>
      )}

      <nav aria-label="Atalhos" className="flex flex-wrap gap-2">
        <BotaoLink to="/animais">Plantel</BotaoLink>
        <BotaoLink to="/visitas">Visitas</BotaoLink>
        <BotaoLink to="/dados">Dados e relatórios</BotaoLink>
        <BotaoLink to="/comparar">Comparar</BotaoLink>
      </nav>
    </>
  );
}

function DoDono({ plantel, fichas, capas }: { plantel: Animal[]; fichas: Avaliacao[]; capas: Map<string, Midia> }) {
  const fechadas = fichas.filter(fechada).sort((x, y) => y.visit_date.localeCompare(x.visit_date));
  const avaliados = new Set(fechadas.map((a) => a.animal_id));
  const recentes = [...new Map(fechadas.map((a) => [a.animal_id, a])).values()].slice(0, 6);
  const nome = new Map(plantel.map((a) => [a.id, a]));
  const hoje = hojeIso();

  return (
    <>
      <section className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda sm:grid-cols-3">
        <Indicador rotulo="Animais" valor={plantel.length} />
        <Indicador rotulo="Avaliados" valor={avaliados.size} />
        <Indicador rotulo="Última avaliação" valor={fechadas[0] ? formatarData(fechadas[0].visit_date) : '—'} />
      </section>

      <Painel titulo="Avaliados recentemente" corpo="p-0 sm:p-4" acoes={<BotaoLink to="/animais">Ver o plantel</BotaoLink>}>
        {recentes.length === 0 ? (
          <p className="p-4 text-sm text-apagado sm:p-0">Nenhuma avaliação fechada ainda.</p>
        ) : (
          // Celular: linha compacta com miniatura (uma foto grande por animal e rolagem demais).
          // Do sm em diante, o cartao com a foto.
          <ul className="divide-y divide-borda sm:grid sm:grid-cols-2 sm:gap-4 sm:divide-y-0 lg:grid-cols-3">
            {recentes.map((a) => {
              const animal = nome.get(a.animal_id);
              if (!animal) return null;
              return (
                <li key={a.id} className="flex items-center sm:block sm:overflow-hidden sm:rounded-(--radius-painel) sm:border sm:border-borda">
                  <Link
                    to={`/animais/${animal.id}?ficha=${a.id}`}
                    className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 active:bg-destaque-fundo sm:block sm:p-0 sm:hover:bg-destaque-fundo/40"
                  >
                    <FotoDaMidia
                      midia={capas.get(animal.id)}
                      alt={animal.name}
                      className="size-14 shrink-0 rounded-(--radius-controle) sm:aspect-3/2 sm:size-auto sm:w-full sm:rounded-none"
                    />
                    <div className="min-w-0 sm:px-3 sm:py-2">
                      <p className="font-serif leading-snug font-semibold text-marca">{animal.name}</p>
                      <p className="text-xs text-apagado">
                        {idadeDoAnimal(animal, hoje)} · avaliado em {formatarData(a.visit_date)}
                      </p>
                    </div>
                  </Link>
                  <div className="shrink-0 pr-2 sm:border-t sm:border-borda sm:px-3 sm:py-2">
                    <Link
                      to={`/animais/${animal.id}/laudo?ficha=${a.id}`}
                      className="flex min-h-11 items-center px-2 text-sm font-medium text-destaque-texto hover:underline sm:inline-flex sm:min-h-0 sm:px-0"
                    >
                      <span className="sm:hidden">Laudo</span>
                      <span className="hidden sm:inline">Abrir o laudo</span>
                    </Link>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Painel>
    </>
  );
}
